import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BottleneckComparisonChart } from '@/components/outputs/charts/BottleneckComparisonChart';
import { GpuMemoryAllocationChart } from '@/components/outputs/charts/GpuMemoryAllocationChart';
import { ThroughputUtilizationChart } from '@/components/outputs/charts/ThroughputUtilizationChart';
import { formatNumber, formatDecimal } from '@/components/sizing/advancedInsightsShared';
import { CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import type { SizingNarrativeData } from '@/types/narrative';

type InfrastructureSummaryTabProps = {
  data: SizingNarrativeData;
};

export const InfrastructureSummaryTab: React.FC<InfrastructureSummaryTabProps> = ({ data }) => {
  const { throughput, kv, model, finalPlan, serverConfig, fleetCapacity } = data;

  const limitingMap: Record<string, 'prompt' | 'decode' | 'kv' | 'none'> = {
    throughput: throughput.gpusForInput >= throughput.gpusForOutput ? 'prompt' : 'decode',
    memory: 'kv',
    model: 'none',
  };

  const limiting = limitingMap[finalPlan.limitingConstraint] || 'none';

  return (
    <div className="space-y-6">
      {/* Infrastructure Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Infrastructure Configuration
          </CardTitle>
          <CardDescription>Hardware specifications and deployment topology</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total GPUs</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(fleetCapacity.totalGpus)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Servers</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(fleetCapacity.totalServers)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">GPUs per Server</p>
              <p className="text-2xl font-bold text-foreground">{serverConfig.acceleratorsPerServer}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">HBM per GPU</p>
              <p className="text-2xl font-bold text-foreground">
                {serverConfig.hbmBytesPerGpu ? `${formatDecimal(serverConfig.hbmBytesPerGpu / (1024 ** 3), 0)} GiB` : 'N/A'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Usable KV Memory/GPU</p>
              <p className="text-2xl font-bold text-foreground">
                {serverConfig.usableBytesPerGpu ? `${formatDecimal(serverConfig.usableBytesPerGpu / (1024 ** 3), 1)} GiB` : 'N/A'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Model Weights</p>
              <p className="text-2xl font-bold text-foreground">{formatDecimal(model.weightsGiBTotal, 1)} GiB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GPU Memory & Throughput Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <GpuMemoryAllocationChart
          gpuMemory={data.gpuMemory}
          personaKvBreakdowns={data.personaKvBreakdowns}
        />
        <ThroughputUtilizationChart data={data} />
      </div>

      {/* Bottleneck Analysis Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Prompt Throughput */}
        <Card className={limiting === 'prompt' ? 'border-destructive' : ''}>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              Prompt Throughput
              {limiting === 'prompt' && <Badge variant="destructive">Bottleneck</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Required TPS:</span>
              <span className="font-medium">{formatNumber(throughput.inputTpsRequired)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TPS per GPU:</span>
              <span className="font-medium">{formatNumber(throughput.inputTpsPerGpu)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">GPUs Needed:</span>
              <span className="font-bold text-lg">{formatNumber(Math.ceil(throughput.gpusForInput))}</span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground">Utilization:</span>
              <span className="font-medium">
                {formatDecimal((throughput.gpusForInput / finalPlan.finalGpuCount) * 100, 1)}%
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Decode Throughput */}
        <Card className={limiting === 'decode' ? 'border-destructive' : ''}>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              Decode Throughput
              {limiting === 'decode' && <Badge variant="destructive">Bottleneck</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Required TPS:</span>
              <span className="font-medium">{formatNumber(throughput.outputTpsRequired)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TPS per GPU:</span>
              <span className="font-medium">{formatNumber(throughput.outputTpsPerGpu)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">GPUs Needed:</span>
              <span className="font-bold text-lg">{formatNumber(Math.ceil(throughput.gpusForOutput))}</span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground">Utilization:</span>
              <span className="font-medium">
                {formatDecimal((throughput.gpusForOutput / finalPlan.finalGpuCount) * 100, 1)}%
              </span>
            </div>
          </CardContent>
        </Card>

        {/* KV Memory */}
        <Card className={limiting === 'kv' ? 'border-destructive' : ''}>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              KV Memory
              {limiting === 'kv' && <Badge variant="destructive">Bottleneck</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">P95 Sessions:</span>
              <span className="font-medium">{formatNumber(kv.kvSessionsP95)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total KV GiB:</span>
              <span className="font-medium">{formatDecimal(kv.kvGiBTotal, 1)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">GPUs Needed:</span>
              <span className="font-bold text-lg">{formatNumber(Math.ceil(kv.gpusKv))}</span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground">Utilization:</span>
              <span className="font-medium">
                {formatDecimal((kv.gpusKv / finalPlan.finalGpuCount) * 100, 1)}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottleneck Comparison Chart */}
      <BottleneckComparisonChart
        promptGpus={Math.ceil(throughput.gpusForInput)}
        decodeGpus={Math.ceil(throughput.gpusForOutput)}
        kvGpus={Math.ceil(kv.gpusKv)}
        limitingConstraint={limiting}
      />

      {/* Final Recommendation */}
      <Card className="border-primary/50 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Final Recommendation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total GPU Count</p>
              <p className="text-3xl font-bold text-foreground">{formatNumber(finalPlan.finalGpuCount)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Determined by {finalPlan.limitingConstraint === 'throughput' ? 'throughput' : finalPlan.limitingConstraint === 'memory' ? 'KV memory' : 'model'} constraint
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Server Distribution</p>
              <p className="text-3xl font-bold text-foreground">{formatNumber(finalPlan.finalServerCount)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {serverConfig.displayName ?? 'Servers'} with {finalPlan.gpusPerServer} GPUs each
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">Key Insight:</strong> Your infrastructure is primarily constrained by{' '}
              <strong className="text-foreground">
                {finalPlan.limitingConstraint === 'throughput' ? 'token throughput requirements' : 
                 finalPlan.limitingConstraint === 'memory' ? 'KV cache memory capacity' : 
                 'model size and sharding'}
              </strong>
              . Consider optimizing this area for cost efficiency.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
